import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
import { Choice, TextField } from '../PastPerformance/Fields';
import { ItemsReadOnly } from './SolutionItems';
export function RevisionAttribution({ revision, author }) {
    const date = revision.createdAt?.toDate();
    return /*#__PURE__*/ _jsxs("span", {
        className: "space-x-2 text-sm",
        children: [
            /*#__PURE__*/ _jsx("span", {
                "data-testid": Testids.captureSolution.historyActor,
                "data-user-id": revision.actorUserId,
                children: author?.id === revision.actorUserId ? author.name || author.email || `User ${revision.actorUserId}` : revision.actorUserId ? `User ${revision.actorUserId}` : 'Author not recorded'
            }),
            date ? /*#__PURE__*/ _jsx("time", {
                "data-testid": Testids.captureSolution.historyDate,
                dateTime: date.toISOString(),
                children: date.toLocaleString()
            }) : /*#__PURE__*/ _jsx("span", {
                children: "Date not recorded"
            }),
            /*#__PURE__*/ _jsx("span", {
                children: revision.changeNote || 'No edit reason recorded'
            })
        ]
    });
}
export function SolutionHandoffs({ captureProjectId, revisions, author, renderSolution }) {
    const S = Testids.captureSolution;
    const client = useQueryClient();
    const queryKey = [
        'capture-solution-handoffs',
        captureProjectId
    ];
    const list = useQuery({
        queryKey,
        queryFn: async ()=>(await projectDataClient.listCaptureSolutionHandoffs({
                captureProjectId
            })).handoffs,
        retry: false
    });
    const [editing, setEditing] = useState(false);
    const [solutionId, setSolutionId] = useState('');
    const [selections, setSelections] = useState({});
    const [reason, setReason] = useState('');
    const [opened, setOpened] = useState();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const inFlight = useRef(false);
    const request = useRef();
    const assumptionItems = new Map();
    for (const r of revisions)for (const i of r.items)if (i.kind === 'internal_assumption') assumptionItems.set(i.id, i.text);
    function changed() {
        request.current = undefined;
        setError('');
    }
    async function save() {
        if (inFlight.current || !solutionId) return;
        const assumptionVersions = Object.entries(selections).filter(([, revisionId])=>revisionId).sort(([a], [b])=>a.localeCompare(b)).map(([itemId, revisionId])=>({
                revisionId,
                itemId
            }));
        const payload = {
            captureProjectId,
            solutionRevisionId: solutionId,
            assumptionVersions,
            reason
        };
        const fingerprint = JSON.stringify({
            ...payload,
            actorUserId: author?.id
        });
        if (request.current?.fingerprint !== fingerprint) request.current = {
            fingerprint,
            id: crypto.randomUUID()
        };
        inFlight.current = true;
        setBusy(true);
        setError('');
        try {
            const { handoff } = await projectDataClient.createCaptureSolutionHandoff({
                ...payload,
                requestId: request.current.id
            });
            if (!handoff) throw new Error('The saved handoff was not returned. Retry to reopen it.');
            client.setQueryData(queryKey, (previous = [])=>[
                    handoff,
                    ...previous.filter((h)=>h.id !== handoff.id)
                ]);
            setOpened(handoff);
            setEditing(false);
        } catch (e) {
            setError(`Could not save handoff: ${ConnectError.from(e).rawMessage}. Your selection is preserved; retry saving.`);
        } finally{
            inFlight.current = false;
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-semibold",
                children: "Final RFP handoffs"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm",
                children: "Save an immutable baseline of an exact solution version and explicitly selected internal assumptions. This is upstream intelligence; final solicitation requirements remain authoritative."
            }),
            list.isLoading && /*#__PURE__*/ _jsx("output", {
                children: "Loading saved handoffs…"
            }),
            list.isError && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        role: "alert",
                        children: [
                            "Could not load saved handoffs: ",
                            ConnectError.from(list.error).rawMessage
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": S.handoffRetry,
                        onClick: ()=>list.refetch(),
                        children: "Retry loading handoffs"
                    })
                ]
            }),
            list.data?.length === 0 && /*#__PURE__*/ _jsx("p", {
                children: "No handoffs saved. Select versions to create a baseline."
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: list.data?.map((h)=>/*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx(RevisionAttribution, {
                                revision: {
                                    actorUserId: h.actorUserId,
                                    createdAt: h.createdAt,
                                    changeNote: h.reason
                                },
                                author: author
                            }),
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                "data-testid": S.handoffOpen,
                                onClick: ()=>setOpened(h),
                                children: [
                                    "Open saved handoff · solution revision ",
                                    String(h.solutionRevision?.revision ?? '')
                                ]
                            })
                        ]
                    }, h.id))
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": S.handoffCreate,
                disabled: !revisions.length || busy,
                onClick: ()=>{
                    setOpened(undefined);
                    setEditing(true);
                },
                children: "Select versions for handoff"
            }),
            !revisions.length && /*#__PURE__*/ _jsx("p", {
                children: "Save a solution revision before creating a handoff."
            }),
            editing && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsxs("fieldset", {
                        disabled: busy,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(Choice, {
                                label: "Solution version",
                                required: true,
                                testId: S.handoffSolutionVersion,
                                value: solutionId,
                                onChange: (v)=>{
                                    changed();
                                    setSolutionId(v);
                                },
                                options: [
                                    {
                                        value: '',
                                        label: 'Choose a saved solution version'
                                    },
                                    ...revisions.map((r)=>({
                                            value: r.id,
                                            label: `Revision ${r.revision} · ${r.changeNote || r.approach}`
                                        }))
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm",
                                children: "Assumptions are selected independently. Leave each at “Do not include” to exclude it."
                            }),
                            [
                                ...assumptionItems
                            ].map(([id, text])=>/*#__PURE__*/ _jsx(Choice, {
                                    label: `Assumption: ${text}`,
                                    testId: S.handoffAssumptionVersion,
                                    value: selections[id] ? `${id}@${selections[id]}` : '',
                                    onChange: (v)=>{
                                        changed();
                                        setSelections((s)=>({
                                                ...s,
                                                [id]: v ? v.slice(id.length + 1) : ''
                                            }));
                                    },
                                    options: [
                                        {
                                            value: '',
                                            label: 'Do not include'
                                        },
                                        ...revisions.flatMap((r)=>r.items.filter((i)=>i.id === id && i.kind === 'internal_assumption').map((i)=>({
                                                    value: `${id}@${r.id}`,
                                                    label: `Revision ${r.revision} · ${i.text}`
                                                })))
                                    ]
                                }, id)),
                            !assumptionItems.size && /*#__PURE__*/ _jsx("p", {
                                children: "No typed internal assumptions are available. You can save a solution-only handoff."
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Why these versions? (optional)",
                                testId: S.handoffReason,
                                value: reason,
                                onChange: (v)=>{
                                    changed();
                                    setReason(v);
                                }
                            })
                        ]
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        "data-testid": S.handoffError,
                        children: error
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": S.handoffSave,
                        disabled: busy || !solutionId,
                        onClick: save,
                        children: busy ? 'Saving handoff…' : error ? 'Retry saving handoff' : 'Save handoff'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": S.handoffCancel,
                        disabled: busy,
                        onClick: ()=>setEditing(false),
                        children: "Cancel — keep selection"
                    })
                ]
            }),
            opened && /*#__PURE__*/ _jsxs("div", {
                "data-testid": S.handoffBaseline,
                "data-handoff-id": opened.id,
                "data-solution-revision-id": opened.solutionRevisionId,
                "data-assumption-id": opened.assumptions[0]?.version?.itemId,
                "data-assumption-revision-id": opened.assumptions[0]?.version?.revisionId,
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsxs("h4", {
                        className: "font-semibold",
                        children: [
                            "Saved handoff · solution revision ",
                            String(opened.solutionRevision?.revision ?? '')
                        ]
                    }),
                    /*#__PURE__*/ _jsx(RevisionAttribution, {
                        revision: {
                            actorUserId: opened.actorUserId,
                            createdAt: opened.createdAt,
                            changeNote: opened.reason
                        },
                        author: author
                    }),
                    opened.solutionRevision && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                children: "Historical solution context"
                            }),
                            /*#__PURE__*/ _jsx(RevisionAttribution, {
                                revision: opened.solutionRevision,
                                author: author
                            }),
                            renderSolution(opened.solutionRevision)
                        ]
                    }),
                    /*#__PURE__*/ _jsx("h4", {
                        children: "Explicitly selected assumptions"
                    }),
                    !opened.assumptions.length && /*#__PURE__*/ _jsx("p", {
                        children: "No assumptions selected."
                    }),
                    opened.assumptions.map((a)=>/*#__PURE__*/ _jsxs("div", {
                            "data-assumption-id": a.version?.itemId,
                            "data-assumption-revision-id": a.version?.revisionId,
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs",
                                    children: revisions.some((r)=>r.id === a.version?.revisionId) ? `Source revision ${revisions.find((r)=>r.id === a.version?.revisionId)?.revision}` : 'Saved source version'
                                }),
                                /*#__PURE__*/ _jsx(RevisionAttribution, {
                                    revision: a,
                                    author: author
                                }),
                                a.item && /*#__PURE__*/ _jsx(ItemsReadOnly, {
                                    items: [
                                        a.item
                                    ]
                                })
                            ]
                        }, a.version?.itemId)),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": S.handoffBack,
                        onClick: ()=>setOpened(undefined),
                        children: "Back to working solution"
                    })
                ]
            })
        ]
    });
}
